import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { 
  AlertTriangle, 
  RefreshCw, 
  Home, 
  Bug, 
  ChevronDown, 
  ChevronUp,
  Copy,
  ExternalLink
} from 'lucide-react';
import { globalErrorHandler, ErrorParser } from '../lib/errorHandler';
import i18n from '../i18n';
import { useTranslation } from 'react-i18next';

const t = (key, options) => i18n.t(key, options);

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      enhancedError: null,
      showDetails: false,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Parse the error with our enhanced error system
    const enhancedError = ErrorParser.parseJavaScriptError(error, errorInfo);
    
    // Handle with global error handler
    const handledError = globalErrorHandler.handleError(enhancedError, {
      component: this.props.name || 'ErrorBoundary',
      errorBoundary: true,
      retryCount: this.state.retryCount
    });

    this.setState({
      error,
      errorInfo,
      enhancedError: handledError
    });

    // Report to external service if configured
    if (this.props.onError) {
      this.props.onError(handledError, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      enhancedError: null,
      showDetails: false,
      retryCount: prevState.retryCount + 1
    }));
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  toggleDetails = () => {
    this.setState(prevState => ({
      showDetails: !prevState.showDetails
    }));
  };

  copyErrorToClipboard = () => {
    const errorText = `
Error ID: ${this.state.enhancedError?.errorId}
Type: ${this.state.enhancedError?.type}
Message: ${this.state.error?.message}
Stack: ${this.state.error?.stack}
Component Stack: ${this.state.errorInfo?.componentStack}
Timestamp: ${this.state.enhancedError?.timestamp}
    `.trim();

    navigator.clipboard.writeText(errorText).then(() => {
      alert(t('errorBoundary.copied'));
    });
  };

  render() {
    if (this.state.hasError) {
      const { enhancedError, error, errorInfo, showDetails, retryCount } = this.state;
      const { 
        fallback: CustomFallback,
        showRetry = true,
        showReload = true,
        showGoHome = true,
        maxRetries = 3
      } = this.props;

      // If custom fallback is provided, use it
      if (CustomFallback) {
        return (
          <CustomFallback
            error={enhancedError || error}
            errorInfo={errorInfo}
            retry={this.handleRetry}
            reload={this.handleReload}
            goHome={this.handleGoHome}
          />
        );
      }

      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="max-w-2xl w-full">
            <Card className="border-destructive/40 bg-destructive/5">
              <CardHeader>
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <AlertTriangle className="h-8 w-8 text-destructive" />
                  </div>
                  <div>
                    <CardTitle className="text-foreground">
                      {enhancedError?.title || t('errorBoundary.titleFallback')}
                    </CardTitle>
                    <CardDescription className="text-muted-foreground mt-1">
                      {enhancedError?.message || t('errorBoundary.descFallback')}
                    </CardDescription>
                  </div>
                </div>
                
                {enhancedError && (
                  <div className="flex items-center space-x-2 mt-3">
                    <Badge variant="destructive" className="text-xs">
                      {enhancedError.type}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      ID: {enhancedError.errorId}
                    </Badge>
                    {retryCount > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {t('errorBoundary.attempt', { n: retryCount })}
                      </Badge>
                    )}
                  </div>
                )}
              </CardHeader>

              <CardContent className="space-y-4">
                {/* Error Actions */}
                <div className="flex flex-wrap gap-2">
                  {showRetry && retryCount < maxRetries && (
                    <Button onClick={this.handleRetry} className="flex-1 min-w-[120px]">
                      <RefreshCw className="mr-2 h-4 w-4" />
                      {t('errors.retry')}
                    </Button>
                  )}
                  
                  {showReload && (
                    <Button 
                      onClick={this.handleReload} 
                      variant="outline"
                      className="flex-1 min-w-[120px]"
                    >
                      <RefreshCw className="mr-2 h-4 w-4" />
                      {t('errors.reloadPage')}
                    </Button>
                  )}
                  
                  {showGoHome && (
                    <Button 
                      onClick={this.handleGoHome} 
                      variant="outline"
                      className="flex-1 min-w-[120px]"
                    >
                      <Home className="mr-2 h-4 w-4" />
                      {t('errorBoundary.goHome')}
                    </Button>
                  )}
                </div>

                <Separator />

                {/* Error Details Toggle */}
                <div>
                  <Button
                    onClick={this.toggleDetails}
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  >
                    <Bug className="mr-2 h-4 w-4" />
                    {t('errorBoundary.techDetails')}
                    {showDetails ? (
                      <ChevronUp className="ml-2 h-4 w-4" />
                    ) : (
                      <ChevronDown className="ml-2 h-4 w-4" />
                    )}
                  </Button>

                  {showDetails && (
                    <div className="mt-4 space-y-4">
                      {/* Error Information */}
                      <div className="bg-card rounded-lg p-4 border border-border">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <strong>{t('errorBoundary.errorType')}</strong>
                            <p className="text-muted-foreground">{enhancedError?.type || 'JAVASCRIPT_ERROR'}</p>
                          </div>
                          <div>
                            <strong>{t('errorBoundary.severity')}</strong>
                            <p className="text-muted-foreground">{enhancedError?.severity || 'HIGH'}</p>
                          </div>
                          <div>
                            <strong>{t('errorBoundary.timestamp')}</strong>
                            <p className="text-muted-foreground">
                              {enhancedError?.timestamp ?
                                new Date(enhancedError.timestamp).toLocaleString(i18n.language) :
                                new Date().toLocaleString(i18n.language)
                              }
                            </p>
                          </div>
                          <div>
                            <strong>{t('errorBoundary.errorId')}</strong>
                            <p className="text-muted-foreground font-mono">{enhancedError?.errorId || 'N/A'}</p>
                          </div>
                        </div>
                      </div>

                      {/* Error Message */}
                      {error?.message && (
                        <div className="bg-card rounded-lg p-4 border border-border">
                          <strong className="block mb-2">{t('errorBoundary.errorMessage')}</strong>
                          <pre className="text-sm text-muted-foreground whitespace-pre-wrap bg-muted/50 p-2 rounded">
                            {error.message}
                          </pre>
                        </div>
                      )}

                      {/* Stack Trace */}
                      {error?.stack && (
                        <div className="bg-card rounded-lg p-4 border border-border">
                          <strong className="block mb-2">{t('errorBoundary.stackTrace')}</strong>
                          <pre className="text-xs text-muted-foreground whitespace-pre-wrap bg-muted/50 p-2 rounded max-h-40 overflow-y-auto">
                            {error.stack}
                          </pre>
                        </div>
                      )}

                      {/* Component Stack */}
                      {errorInfo?.componentStack && (
                        <div className="bg-card rounded-lg p-4 border border-border">
                          <strong className="block mb-2">{t('errorBoundary.componentStack')}</strong>
                          <pre className="text-xs text-muted-foreground whitespace-pre-wrap bg-muted/50 p-2 rounded max-h-40 overflow-y-auto">
                            {errorInfo.componentStack}
                          </pre>
                        </div>
                      )}

                      {/* Copy Button */}
                      <div className="flex justify-end">
                        <Button
                          onClick={this.copyErrorToClipboard}
                          variant="outline"
                          size="sm"
                        >
                          <Copy className="mr-2 h-4 w-4" />
                          {t('errorBoundary.copyDetails')}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                <Separator />

                {/* Help Information */}
                <div className="bg-primary/5 rounded-lg p-4 border border-primary/20">
                  <h4 className="font-medium mb-2">{t('errorBoundary.whatNow')}</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• {t('errorBoundary.tryReload')}</li>
                    <li>• {t('errorBoundary.checkInternet')}</li>
                    <li>• {t('errorBoundary.contactSupport')}</li>
                    {enhancedError?.canRetry && retryCount < maxRetries && (
                      <li>• {t('errorBoundary.clickRetry')}</li>
                    )}
                  </ul>
                </div>

                {/* Support Contact */}
                <div className="text-center text-sm text-muted-foreground">
                  <p>
                    {t('errorBoundary.needHelp')}{' '}
                    <span className="font-mono font-medium">
                      {enhancedError?.errorId || 'N/A'}
                    </span>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Simple Error Fallback Component
export const SimpleErrorFallback = ({ error, retry, reload }) => {
  const { t: tr } = useTranslation();
  return (
  <div className="flex items-center justify-center min-h-[200px] p-4">
    <Card className="max-w-md w-full border-destructive/40">
      <CardContent className="pt-6">
        <div className="text-center space-y-4">
          <AlertTriangle className="h-12 w-12 text-destructive mx-auto" />
          <div>
            <h3 className="font-semibold">{tr('errorBoundary.componentError')}</h3>
            <p className="text-sm text-muted-foreground mt-1">
              {error?.message || tr('errorBoundary.componentErrorDesc')}
            </p>
          </div>
          <div className="flex space-x-2">
            {retry && (
              <Button onClick={retry} size="sm" className="flex-1">
                <RefreshCw className="mr-2 h-4 w-4" />
                {tr('errors.retry')}
              </Button>
            )}
            {reload && (
              <Button onClick={reload} variant="outline" size="sm" className="flex-1">
                <RefreshCw className="mr-2 h-4 w-4" />
                {tr('errorBoundary.reload')}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
  );
};

// HOC to wrap components with error boundary
export const withErrorBoundary = (Component, errorBoundaryProps = {}) => {
  const WrappedComponent = (props) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );
  
  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

export default ErrorBoundary; 